<script setup lang="ts">
import { onMounted, watch } from 'vue'
import { RouterView } from 'vue-router'

import { useAuthStore } from '@/features/auth/stores/auth-store'
import { useSystemConfigStore } from '@/features/settings/stores/system-config-store'
import { useTagModelStore } from '@/features/settings/stores/tag-model-store'
import ChunkReloadDialog from '@/shared/components/ChunkReloadDialog.vue'
import { WEB_PUSH_ENABLED } from '@/shared/config/features'

const auth = useAuthStore()
const systemConfig = useSystemConfigStore()
const tagModels = useTagModelStore()

// Reload trang: accessToken còn trong localStorage nhưng profile (in-memory) bị mất.
// Refetch /me để khôi phục roles/permissions/tenant — cần cho sidebar, isSuperAdmin
// (tab "Quản lý công ty"), guard route super-admin, v.v.
onMounted(() => {
  if (auth.isAuthenticated && !auth.profile) {
    void auth.fetchMe()
  }
  if (WEB_PUSH_ENABLED) {
    // Re-register this browser for push on reload if it already granted permission
    // (silent — never prompts). Best-effort; push must not break app start.
    if (auth.isAuthenticated) {
      void import('@/core/fcm/push-manager').then((m) => m.syncPush())
    }
  } else {
    // Feature off — retract any worker a previous build installed. Runs for signed-out
    // visitors too: the worker outlives the session, so it must not be gated on auth.
    void retireLegacyPushServiceWorker()
  }
  // SYS-map-provider: load client-facing system config once (map provider + future
  // web/all values). Best-effort; on error mapProvider falls back to 'osm'.
  if (auth.isAuthenticated) {
    void systemConfig.hydrate()
  }
})

/**
 * Switching web push off is not enough on its own: a browser that once turned the
 * toggle ON still has `firebase-messaging-sw.js` INSTALLED, and an installed service
 * worker keeps showing background notifications by itself — no page, no token sync
 * needed.
 *
 * Dropping the vite plugin does NOT retract it either. `deploy/deploy-web.sh` uploads with
 * `aws s3 sync` and no `--delete`, so the object from the last push-enabled deploy stays in
 * the bucket and keeps being served (Cache-Control immutable). The worker updates fine and
 * goes on working; removing the plugin only stops NEW installs, which the client guards
 * already prevent. Explicit unregistration is the only thing that retracts it.
 *
 * Only ever removes our own push worker, so a browser that never enabled push finds
 * nothing and no-ops. Flipping VITE_ENABLE_WEB_PUSH back on stops this from running
 * and the normal enable flow re-registers the worker.
 */
async function retireLegacyPushServiceWorker(): Promise<void> {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return
  try {
    const registrations = await navigator.serviceWorker.getRegistrations()
    await Promise.all(
      registrations
        .filter((r) =>
          (r.active ?? r.waiting ?? r.installing)?.scriptURL.includes('firebase-messaging-sw'),
        )
        .map((r) => r.unregister()),
    )
  } catch {
    // Best-effort cleanup — must never break app start.
  }
}

// SYS-tag-model: load the device model config once per session. It gates the
// battery/buzzer UI on asset & card screens and colors the dashboard slices.
//
// A `watch`, NOT onMounted: login is an SPA `router.push`, so App.vue (the root) mounted
// long before — with `isAuthenticated` still false. Hydrating only in onMounted would
// leave anyone who signs in without a page reload on the built-in fallback models, and a
// model the admin added would silently show no battery. `immediate` covers the reload
// case (token already in storage). Best-effort: on error the registry keeps its fallback.
watch(
  () => auth.isAuthenticated,
  async (authenticated) => {
    if (!authenticated) return
    // The models live inside client-config, so load that first, then publish them.
    await systemConfig.hydrate()
    tagModels.hydrate()
  },
  { immediate: true },
)
</script>

<template>
  <div class="min-h-screen bg-gray-50 text-gray-900 antialiased">
    <RouterView />
    <ChunkReloadDialog />
  </div>
</template>
